import { createContext, useState, useContext, useEffect } from "react"
import FavoriteService from "../services/favoriteService"
import { useAuth } from "./AuthContext"

const FavoritesContext = createContext()

export const useFavorites = () => useContext(FavoritesContext)

export const FavoritesProvider = ({ children }) => {
  const { user } = useAuth()
  const [favoriteTemples, setFavoriteTemples] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Load favorites when user logs in
  useEffect(() => {
    if (user) {
      loadFavorites()
    } else {
      // Clear favorites when user logs out
      setFavoriteTemples([])
    }
  }, [user])

  const loadFavorites = async () => {
    if (!user) return

    setLoading(true)
    setError(null)

    try {
      // Load favorite temples
      const templesResponse = await FavoriteService.getFavoriteTemples()
       // Ensure templesResponse is an array
    if (Array.isArray(templesResponse)) {
      setFavoriteTemples(templesResponse)
    } else {
      setFavoriteTemples([])  // or handle error case
    }
    } catch (err) {
      setError("Failed to load favorites")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // Check if a temple is in favorites
  const isTempleInFavorites = (templeId) => {
    return favoriteTemples.some((temple) => temple._id === templeId)
  }


  // Add temple to favorites
  const addTempleToFavorites = async (templeId) => {
    if (!user) return

    setLoading(true)
    setError(null)

    try {
      await FavoriteService.addFavoriteTemple(templeId)
      // Reload favorites to get the updated list
      await loadFavorites()
      return true
    } catch (err) {
      setError("Failed to add temple to favorites")
      console.error(err)
      return false
    } finally {
      setLoading(false)
    }
  }

  // Remove temple from favorites
  const removeTempleFromFavorites = async (templeId) => {
    if (!user) return

    setLoading(true)
    setError(null)

    try {
      await FavoriteService.removeFavoriteTemple(templeId)
      // Update local state without reloading
      setFavoriteTemples(favoriteTemples.filter((temple) => temple._id !== templeId))
      return true
    } catch (err) {
      setError("Failed to remove temple from favorites")
      console.error(err)
      return false
    } finally {
      setLoading(false)
    }
  }


  // Toggle temple favorite status
  const toggleTempleInFavorites = async (templeId) => {
    if (isTempleInFavorites(templeId)) {
      return removeTempleFromFavorites(templeId)
    } else {
      return addTempleToFavorites(templeId)
    }
  }

  return (
    <FavoritesContext.Provider
      value={{
        favoriteTemples,
        loading,
        error,
        isTempleInFavorites,
        addTempleToFavorites,
        removeTempleFromFavorites,
        toggleTempleInFavorites,
        loadFavorites,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  )
}
